import { memo, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ExternalLink, Loader2, Sparkles, Users } from "lucide-react";
import type { FirefliesTranscriptSummary } from "@/types/fireflies";
import { cn } from "@/lib/utils";

function formatMeetingDate(dateMs: number | null | undefined): string {
  if (!dateMs) return "Unknown date";
  const d = new Date(dateMs);
  if (Number.isNaN(d.getTime())) return "Unknown date";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatDuration(durationMin: number | null | undefined): string {
  if (!durationMin) return "";
  const min = Math.round(durationMin);
  return `${min} min`;
}

export interface FirefliesMeetingRowProps {
  meeting: FirefliesTranscriptSummary;
  selected: boolean;
  batchChecked: boolean;
  formatting: boolean;
  formatBusy: boolean;
  onSelect: (id: string) => void;
  onBatchToggle: (id: string) => void;
  onFormat: (id: string) => void;
  indexBadge?: React.ReactNode;
}

export const FirefliesMeetingRow = memo(function FirefliesMeetingRow({
  meeting,
  selected,
  batchChecked,
  formatting,
  formatBusy,
  onSelect,
  onBatchToggle,
  onFormat,
  indexBadge,
}: FirefliesMeetingRowProps) {
  const handleSelect = useCallback(() => onSelect(meeting.id), [meeting.id, onSelect]);
  const handleFormat = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onFormat(meeting.id);
    },
    [meeting.id, onFormat]
  );
  const handleBatchToggle = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onBatchToggle(meeting.id);
    },
    [meeting.id, onBatchToggle]
  );

  const attendeeCount =
    meeting.meeting_attendees?.length ?? meeting.participants?.length ?? 0;
  const shortSummary =
    meeting.summary?.short_summary || meeting.summary?.gist || null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSelect();
        }
      }}
      className={cn(
        "flex flex-col gap-2 rounded-lg border p-4 transition-colors cursor-pointer",
        selected ? "border-primary bg-primary/5" : "hover:bg-muted/50",
        batchChecked && !selected && "border-primary/40 bg-primary/[0.02]"
      )}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          className="mt-1 shrink-0"
          checked={batchChecked}
          disabled={formatBusy}
          onClick={handleBatchToggle}
          aria-label={`Select ${meeting.title || "meeting"} for batch format`}
        />

        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-medium truncate">{meeting.title || "Untitled meeting"}</h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                {formatMeetingDate(meeting.date)}
                {meeting.duration ? ` · ${formatDuration(meeting.duration)}` : ""}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {indexBadge}
              {meeting.transcript_url && (
                <Button
                  variant="ghost"
                  size="icon"
                  asChild
                  onClick={(e) => e.stopPropagation()}
                >
                  <a
                    href={meeting.transcript_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open in Fireflies"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                disabled={formatBusy && !formatting}
                onClick={handleFormat}
              >
                {formatting ? (
                  <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-1.5" />
                )}
                {formatting ? "Formatting…" : "Format"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {shortSummary && (
        <p className="text-sm text-muted-foreground line-clamp-2 pl-7">{shortSummary}</p>
      )}

      <div className="flex flex-wrap gap-2 pl-7">
        {attendeeCount > 0 && (
          <Badge variant="outline" className="gap-1">
            <Users className="h-3 w-3" />
            {attendeeCount} attendee{attendeeCount !== 1 ? "s" : ""}
          </Badge>
        )}
        {meeting.host_email && (
          <Badge variant="outline" className="font-normal">
            Host: {meeting.host_email}
          </Badge>
        )}
      </div>
    </div>
  );
});
